import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Grid2, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { IconTrash, IconEdit, IconEye, IconDotsVertical, IconPlus, IconSearch, IconRestore } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import StatusChip from "@components/chip/statusChip";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { ACTION } from "@utils/constant";
import { formatDate } from "@utils/formatter";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { applyFilters, createSearchFilter, createCustomFilter, createDateRangeFilter } from "@utils/filter";

const defaultQueryFields = {
  search: "",
  postNumber: "",
  category: null,
  status: null,
  dateFrom: "",
  dateTo: "",
};

export default function Post() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const { user, isCurrentModuleAdmin } = useAuthentication()

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      search: "",
      postNumber: "",
      category: null,
      status: null,
      dateFrom: "",
      dateTo: "",
    }
  })

  const [searchQuery, setSearchQuery] = useState(defaultQueryFields)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);

  const [action, setAction] = useState("")
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [selectedRow, setSelectedRow] = useState(null)

  //==========================DEMO==========================
  // const isFetching = false
  // const [data, setData] = useState(DUMMY_DATA)


  const handleConfirmSubmit = () => {
    const key = "postGuid";  /// Change This

    // if (action === ACTION.DELETE) {
    //   const updatedItems = data.filter((item) => item[key] !== selectedRow[key]);
    //   setData(updatedItems);
    //   setOpenConfirmDialog(false)
    // }
  }
  //==========================DEMO==========================

  const { isFetching, error, data } = useQuery({
    queryKey: ['getPost'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getPost),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
    },
  })

  // console.log(data)

  const categoryOptions = useQuery({
    queryKey: ['getPostCategoryList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getPostCategoryList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        categoryGuid: item.parameterGuid,
        categoryName: item.valueEng
      }));
    },
  })

  const postStatusOptions = useQuery({
    queryKey: ['getPostStatusList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getPostStatusList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        statusGuid: item.parameterGuid,
        status: item.valueEng
      }));
    },
  })

  // console.log(data)

  //================================Table================================

  const handleClickDelete = () => {
    setAction(ACTION.DELETE);
    setOpenConfirmDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedRow(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          component={RouterLink}
          to={`view?id=${selectedRow?.postGuid}`}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        {(isCurrentModuleAdmin && selectedRow?.eventStatus.valueEng !== "Voided") && [
        <MenuItem
          component={RouterLink}
          to={`edit?id=${selectedRow?.postGuid}`}
          key={'edit'}
        >
          <ListItemIcon>
            <IconEdit size={20}/>
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        ]}
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================
      
  const handleClickSearch = (data) => {
    setSearchQuery({...data,
      status: data?.status?.statusGuid,
      category: data?.category?.categoryGuid
    })
  }

  const handleClickReset = () => {
    reset(defaultQueryFields)
    setSearchQuery(defaultQueryFields)
  }

  const filters = [
    createSearchFilter(searchQuery.search, ["postName"]),
    createSearchFilter(searchQuery.postNumber, ["postNumber"]),
    createCustomFilter(searchQuery.status, 'eventStatus.parameterGuid', true),
    createCustomFilter(searchQuery.category, 'category.parameterGuid', true),
    createDateRangeFilter(searchQuery.dateFrom, searchQuery.dateTo, 'postDate')
  ];

  const filteredData = applyFilters(data, filters);

  //======================================================================

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          isCurrentModuleAdmin &&
            <CustomButton component={RouterLink} to={"create"} startIcon={<IconPlus size={13} />}>Create</CustomButton>
        }
      />
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={{ xs: 12 }}>
            <Typography
              fontSize={"0.9rem"}
              color={"#202020"}
              fontWeight={600}
            >
              Search
            </Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="search"
              control={control}
              label="Search"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="postNumber"
              control={control}
              label="Post No"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="category"
              control={control}
              label="Category"
              options={categoryOptions.data}
              loading={categoryOptions.isFetching}
              getOptionLabel={(option) => option.categoryName}
              getOptionKey={(option) => option.categoryGuid}
              isOptionEqualToValue={(option, value) => option.categoryGuid === value?.categoryGuid}
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="status"
              control={control}
              label="Status"
              loading={postStatusOptions.isFetching}
              options={postStatusOptions.data}
              getOptionLabel={(option) => option.status}
              getOptionKey={(option) => option.statusGuid}
              isOptionEqualToValue={(option, value) => option.statusGuid === value?.statusGuid}
            />
          </Grid2>

          <Grid2 size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <ControlledDatePicker 
              name="dateFrom"
              control={control}
              label="Date From"
            />
          </Grid2>

          <Grid2 size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <ControlledDatePicker 
              name="dateTo"
              control={control}
              label="Date To"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
            <CustomIconButton aria-label="Reset" color="#999999" onClick={handleClickReset}>
              <IconRestore />
            </CustomIconButton>
            <CustomIconButton aria-label="Search" onClick={handleSubmit(handleClickSearch)}>
              <IconSearch />
            </CustomIconButton>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper
        sx={{
          p: "0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <DataGrid
          loading={isFetching}
          rows={filteredData}
          localeText={{ noRowsLabel: 'No records found' }}
          getRowId={(row) => row.postGuid}
          columns={[
            {
              field: 'postNumber',
              headerName: 'Post No',
              flex: 1, minWidth: 125,
            },
            {
              field: 'postName',
              headerName: 'Post Name',
              flex: 1, minWidth: 125,
            },
            {
              field: 'category',
              headerName: 'Category',
              flex: 1, minWidth: 125,
              sortable: false,
              valueGetter: (value) => value?.valueEng
            },
            {
              field: 'postDate',
              headerName: 'Post Date',
              flex: 1, minWidth: 125,
              valueGetter: (value) => formatDate(value),
            },
            {
              field: 'applicationPeriod',
              headerName: 'Application Period',
              flex: 1, minWidth: 180,
              sortable: false,
              valueGetter: (value, row) => `${formatDate(row.applyStartDate)} - ${formatDate(row.applyCloseDate)}`,
            },
            {
              field: 'eventPeriod',
              headerName: 'Event Period',
              flex: 1, minWidth: 180,
              sortable: false,
              valueGetter: (value, row) => `${formatDate(row.eventStartDate)} - ${formatDate(row.eventEndDate)}`,
            },
            {
              field: 'applicationReceivedCount',
              headerName: 'Apply Received',
              flex: 1, minWidth: 125,
            },
            {
              field: 'applicationWithApproveStatusCount',
              headerName: 'Accepted Qty',
              flex: 1, minWidth: 125,
            },
            {
              field: 'eventStatus',
              headerName: 'Status',
              flex: 1, minWidth: 100,
              sortable: false,
              renderCell: (params) => (
                <StatusChip  status={params.row.eventStatus?.valueEng} />
              ),
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton aria-label="Actions" onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
      
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={handleConfirmSubmit}
        // loading={deleteCategoryMutation.isPending || updateCategoryStatusMutation.isPending}
        type={action === ACTION.DELETE ? "error" : "info"}
        dialogTitle={`${action} Confirmation`}
        dialogDescription={'Are you sure you want to delete the following post?'}
        items={[selectedRow?.postNumber]}
      />
    </div>
  );
}